import React, { useState, useEffect } from "react";
import {
  registeredUser,
  updateUser,
} from "../../../../services/userMangement/userManagement";
import { FaTimes } from "react-icons/fa";
import PasswordInput from "./../../../../components/password";
import {
  FormActions,
  InputField,
} from "../../../../components/Stepper/fields/inputField";
import { handleErrorRedirect } from "../../../../utility/errorRedirect";
import { useNavigate } from "react-router-dom";

const CreateUserModal = ({
  visible,
  fetchUsers,
  onHide,
  type,
  toast,
  selectedUser,
  setLoading,
  loading,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("true");
  const [initialUserData, setInitialUserData] = useState(null);

  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (type === "edit" && selectedUser) {
      const { name, email, is_active } = selectedUser;
      setName(name);
      setEmail(email);
      setStatus(is_active ? "true" : "false");
      setInitialUserData(selectedUser);
    } else {
      setName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setStatus("true");
      setInitialUserData(null);
    }

    setNameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
  }, [type, selectedUser]);

  const validateFields = () => {
    let isValid = true;

    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

    if (!name.trim()) {
      setNameError("Full Name is required");
      isValid = false;
    }

    if (!email.trim()) {
      setEmailError("Email Address is required");
      isValid = false;
    }

    if (type === "create") {
      if (!password) {
        setPasswordError("Password is required");
        isValid = false;
      } else if (!passwordRegex.test(password)) {
        setPasswordError(
          "Password must be at least 8 characters long, include 1 uppercase letter, 1 number, and 1 special character"
        );
        isValid = false;
      }

      if (password !== confirmPassword) {
        setConfirmPasswordError("Passwords do not match");
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    clearErrors();
    const accessToken = JSON.parse(localStorage.getItem("access_token"));
    const payload = {};
    if (name !== initialUserData?.name) payload.name = name;
    if (email !== initialUserData?.email) payload.email = email;
    if (status !== (initialUserData?.is_active ? "true" : "false")) {
      payload.is_active = status === "true";
    }

    if (type === "edit" && Object.keys(payload).length === 0) {
      toast.current.show({
        className: "bg-yellow-700 text-white",
        severity: "warn",
        summary: "Warning",
        detail: "Please update at least one field before saving",
        life: 3000,
      });
      return;
    }

    try {
      let response;
      if (type === "edit" && Object.keys(payload).length > 0) {
        response = await updateUser(payload, accessToken, selectedUser.id);
      } else if (type === "create") {
        response = await registeredUser(name, email, password, accessToken);
      }
      if (response) {
        await fetchUsers();
        toast.current.show({
          className: "bg-green-300 text-white",
          severity: "success",
          summary: "Success",
          detail: response.data.message,
          life: 3000,
        });
        onHide();
      }
    } catch (error) {
      const status = error?.response?.status;
      handleErrorRedirect(status, navigate);
      console.error("Error saving user:", error);
      toast.current.show({
        className: "bg-yellow-700 text-white",
        severity: "warn",
        summary: "Warning",
        detail: error.message,
        life: 3000,
      });
    }
  };

  const handleChangeName = (value) => {
    setName(value);
    if (nameError) setNameError("");
  };

  const handleChangeEmail = (value) => {
    setEmail(value);
    if (emailError) setEmailError("");
  };

  const handleChangePassword = (value) => {
    setPassword(value);
    if (passwordError) setPasswordError("");
  };

  const handleChangeConfirmPassword = (value) => {
    setConfirmPassword(value);
    if (confirmPasswordError) setConfirmPasswordError("");
  };

  if (!visible) return null;

  const resetFields = () => {
    setName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setStatus("true");
    setInitialUserData(null);
  };

  const clearErrors = () => {
    setNameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
  };

  const handleCancel = () => {
    if (type === "edit" && selectedUser) {
      setName(selectedUser.name);
      setEmail(selectedUser.email);
      setStatus(selectedUser.is_active ? "true" : "false");
    } else {
      resetFields();
    }
    clearErrors();
    onHide();
  };

  return (
    <div>
      <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
        <div className="dark:bg-custom-radial bg-[#F6F6F6] md:w-1/3 w-full max-h-[90vh] overflow-y-auto shadow-lg p-6 text-[rgba(255,255,255,1)] rounded-md">
          <div className="field text-2xl flex items-center justify-between text-lightPrimary text-black dark:text-primary-gradient">
            {type === "edit" ? "Edit User Details" : "Create New User"}
            <FaTimes
              onClick={handleCancel}
              className="text-xl cursor-pointer text-lightPrimary dark:text-darkWaring"
            />
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="mt-4 space-y-4">
              <div className="field">
                <InputField
                  label="Full Name*"
                  placeholder="Enter your name"
                  name="name"
                  value={name}
                  onChange={(e) => handleChangeName(e.target.value)}
                  required={true}
                  errors={nameError}
                />
              </div>
              <div className="field">
                <InputField
                  label="Email Address*"
                  placeholder="Enter your Email Address"
                  name="email"
                  value={email}
                  onChange={(e) => handleChangeEmail(e.target.value)}
                  required={true}
                  errors={emailError}
                  disabled={type === "edit"}
                />
              </div>
              {type === "create" && (
                <>
                  <PasswordInput
                    label="Password*"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => handleChangePassword(e.target.value)}
                    required={true}
                    errors={passwordError}
                  />

                  <PasswordInput
                    label="Confirm Password*"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) =>
                      handleChangeConfirmPassword(e.target.value)
                    }
                    required={true}
                    errors={confirmPasswordError}
                  />
                </>
              )}
              {type !== "create" && (
                <div className="flex flex-col gap-3 pt-3">
                  <label
                    htmlFor="statusToggle"
                    className="text-lg font-semibold text-info dark:text-white"
                  >
                    {status === "true" ? "Enabled" : "Disabled"}
                  </label>

                  <button
                    id="statusToggle"
                    type="button"
                    role="switch"
                    aria-checked={status === "true"}
                    onClick={() =>
                      setStatus(status === "true" ? "false" : "true")
                    }
                    className={`relative ml-1 w-11 h-5 flex items-center rounded-full transition-colors duration-300 focus:outline-none ${
                      status === "true" ? "bg-green-500" : "bg-gray-400"
                    }`}
                  >
                    <span
                      className={`absolute w-4 h-4 bg-white left-[2px] rounded-full shadow-md transform transition-transform duration-300 ${
                        status === "true" ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              )}

              <FormActions
                loading={loading}
                onCancel={handleCancel}
                submitType="submit"
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;
